import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Package,
  MessageSquare,
  BarChart3,
  Settings,
  LogOut,
  Heart,
  Loader2
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useVendorProfile, useIsVendor } from '@/hooks/useVendor';
import VendorProfileTab from '@/components/backoffice/VendorProfileTab';
import VendorServicesTab from '@/components/backoffice/VendorServicesTab';
import VendorMessagesTab from '@/components/backoffice/VendorMessagesTab';
import VendorStatsTab from '@/components/backoffice/VendorStatsTab';

type TabId = 'services' | 'messages' | 'analytics' | 'settings';

const BackofficeDashboard = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { data: isVendor, isLoading: checkingRole } = useIsVendor();
  const { data: vendor, isLoading: loadingVendor } = useVendorProfile();
  
  const [activeTab, setActiveTab] = useState<TabId>('services');

  // Redirect if not a vendor
  useEffect(() => {
    if (!checkingRole && !isVendor && user) {
      navigate('/vendor-auth');
    }
  }, [checkingRole, isVendor, user, navigate]);

  // Redirect if not logged in
  useEffect(() => {
    if (!user && !checkingRole) {
      navigate('/vendor-auth');
    }
  }, [user, checkingRole, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navItems = [
    { id: 'services' as TabId, label: 'Mis Servicios', icon: Package },
    { id: 'messages' as TabId, label: 'Mensajes', icon: MessageSquare },
    { id: 'analytics' as TabId, label: 'Estadísticas', icon: BarChart3 },
    { id: 'settings' as TabId, label: 'Mi Perfil', icon: Settings },
  ];

  if (checkingRole || loadingVendor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">No se encontró tu perfil de proveedor</p>
          <Button onClick={() => navigate('/vendor-auth')}>
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'services':
        return <VendorServicesTab vendorId={vendor.id} />;
      case 'messages':
        return <VendorMessagesTab vendorId={vendor.id} />;
      case 'analytics':
        return <VendorStatsTab vendorId={vendor.id} />;
      case 'settings':
        return <VendorProfileTab vendor={vendor} />;
      default:
        return <VendorServicesTab vendorId={vendor.id} />;
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-card border-r border-border p-6 hidden lg:block">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <Heart className="w-6 h-6 text-primary fill-primary" />
          <span className="font-serif text-xl font-semibold text-foreground">
            Mi Boda Perfecta
          </span>
        </Link>

        <nav className="space-y-2">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === item.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button variant="ghost" className="w-full justify-start gap-3" onClick={handleSignOut}>
            <LogOut className="w-5 h-5" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-card border-b border-border p-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary fill-primary" />
            <span className="font-serif font-semibold">Mi Boda Perfecta</span>
          </Link>
          <Button variant="ghost" size="sm" onClick={handleSignOut}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        {/* Mobile Nav */}
        <div className="flex gap-1 mt-4 overflow-x-auto pb-2">
          {navItems.map(item => (
            <Button
              key={item.id}
              variant={activeTab === item.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(item.id)}
              className="shrink-0"
            >
              <item.icon className="w-4 h-4 mr-1" />
              {item.label}
            </Button>
          ))}
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-serif text-3xl font-semibold text-foreground mb-2">
            Bienvenido, {vendor.name}
          </h1>
          <p className="text-muted-foreground">
            Gestiona tu perfil y conecta con parejas.
          </p>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </main>
    </div>
  );
};

export default BackofficeDashboard;
